import { deepEqual } from 'fast-equals';
import { useEffect, useState, useCallback } from 'react';

import {
  ClientCoreParameters,
  ExtensionAvailableInfoMessage,
  JsonFreezeCompatible,
  jsonStringifyFreeze,
} from 'persistable-types';
import { SymAesEncryptedBlob } from 'vault-crypto';

import {
  getLocalStorageEntry,
  LocalStorageEntry,
  LocalStorageEntryNoDefault,
  LocalStorageEntryWithDefault,
  LOCAL_STORAGE_KEYS,
  setLocalStorageEntry,
} from './localStorageData';

export function useLocalStorageState<S>(
  entry: LocalStorageEntry<S>,
  defaultValue: JsonFreezeCompatible<S>,
): [JsonFreezeCompatible<S>, (newState: JsonFreezeCompatible<S>) => void];
export function useLocalStorageState<S>(
  entry: LocalStorageEntryWithDefault<S>,
): [JsonFreezeCompatible<S>, (newState: JsonFreezeCompatible<S>) => void];
export function useLocalStorageState<S>(
  entry: LocalStorageEntryNoDefault<S>,
): [JsonFreezeCompatible<S> | undefined, (newState: JsonFreezeCompatible<S> | undefined) => void];

export function useLocalStorageState<S>(
  entry: LocalStorageEntry<S>,
  defaultValue?: JsonFreezeCompatible<S>,
): [JsonFreezeCompatible<S> | undefined, (newState: JsonFreezeCompatible<S> | undefined) => void] {
  const getValueFromLocalStorage = useCallback(
    () =>
      defaultValue ? getLocalStorageEntry<S>(entry, defaultValue) : getLocalStorageEntry<S>(entry),
    [defaultValue, entry],
  );

  const [value, setValue] = useState<JsonFreezeCompatible<S> | undefined>(getValueFromLocalStorage);

  const setAndSave = useCallback(
    (newValue: JsonFreezeCompatible<S> | undefined) => {
      setValue(newValue);
      setLocalStorageEntry(entry, newValue);
    },
    [entry],
  );

  useEffect(() => {
    function handleStorage(e: StorageEvent) {
      if (e.key === entry.key) {
        const localStorageValue = getValueFromLocalStorage();
        // Only call setter if value from localStorage is actually different from current value to
        // keep value referential identical and avoid unneeded effect runs
        if (!deepEqual(localStorageValue, value)) {
          setValue(localStorageValue);
        }
      }
    }
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [entry.key, getValueFromLocalStorage, value]);

  // Update value stored in local storage when it doesn't match the value we'll serialize to. This
  // can happen e.g. if we update transformBeforeStore for a value between versions
  useEffect(() => {
    if (!entry.transformBeforeStore || value === undefined) {
      return;
    }
    const storedRawEntry: string | undefined = localStorage[entry.key];
    const serializedRawEntry = jsonStringifyFreeze(entry.transformBeforeStore(value));
    if (serializedRawEntry !== storedRawEntry) {
      setAndSave(value);
    }
  }, [entry, setAndSave, value]);

  return [value, setAndSave];
}

export function useEmail() {
  return useLocalStorageState<string>(LOCAL_STORAGE_KEYS.email);
}

export function useClientCoreParameters() {
  return useLocalStorageState<ClientCoreParameters>(LOCAL_STORAGE_KEYS.clientCoreParameters);
}

export function useEncryptedClientCoreParameters() {
  return useLocalStorageState<SymAesEncryptedBlob>(
    LOCAL_STORAGE_KEYS.encryptedClientCoreParameters,
  );
}

export function useCachedExtensionInstalled() {
  return useLocalStorageState<boolean>(LOCAL_STORAGE_KEYS.cachedExtensionInstalled);
}

export function useCachedExtensionAvailableInfoMessage() {
  return useLocalStorageState<ExtensionAvailableInfoMessage>(
    LOCAL_STORAGE_KEYS.cachedExtensionAvailableInfoMessage,
  );
}

export function useCachedNumberOfLogins() {
  return useLocalStorageState<number>(LOCAL_STORAGE_KEYS.cachedNumberOfLogins);
}

export function useDebugVisible(defaultValue: boolean) {
  return useLocalStorageState<boolean>(LOCAL_STORAGE_KEYS.debugVisible, defaultValue);
}

export function useDebugSimulateExtensionAvailable() {
  return useLocalStorageState<boolean>(LOCAL_STORAGE_KEYS.debugSimulateExtensionAvailable, true);
}

export function useDebugSilenceRequestLogging() {
  return useLocalStorageState<boolean>(LOCAL_STORAGE_KEYS.debugSilenceRequestLogging);
}

export function useDebugPaddleWebhookTargetUrl() {
  return useLocalStorageState<string>(LOCAL_STORAGE_KEYS.debugPaddleWebhookTargetUrl);
}

export function useHideRateExtension() {
  return useLocalStorageState<boolean>(LOCAL_STORAGE_KEYS.hideRateExtension);
}

export function useLastActivity() {
  return useLocalStorageState<Date>(LOCAL_STORAGE_KEYS.lastActivity);
}

export function useMenuWidth() {
  return useLocalStorageState<number>(LOCAL_STORAGE_KEYS.menuWidth);
}

export function useMasterWidth() {
  return useLocalStorageState<number>(LOCAL_STORAGE_KEYS.masterWidth);
}
